import { camelize } from "humps";
import truncate from "lodash/truncate";
import { Inline } from "PFComponents/containers";
import { Typography } from "PFComponents/typography";
import { useCustomValueToString } from "PFCore/helpers/custom_value";
import { getJobTitle } from "PFCore/helpers/profile";
import { CustomType, Profile } from "PFCore/types";
import { useCallback, useMemo } from "react";

import css from "./headline.module.scss";

type HeadlineProps = {
  profile: Profile;
  customTypes: CustomType[];
};

export const Headline = ({ profile, customTypes }: HeadlineProps) => {
  const { getCustomValuesListString } = useCustomValueToString();

  const getMatchInfoData = useCallback(
    (sectionKey: string) => {
      const type = customTypes.find(({ sections }) => (sections || []).find(({ key }) => key === sectionKey));
      const customValues = (type && profile[camelize(type.name)]) || [];
      return {
        label: type?.display_as,
        text: customValues.length && getCustomValuesListString(customValues, type?.value_type)
      };
    },
    [getCustomValuesListString, profile, customTypes]
  );

  const jobTitleLabel = useMemo(
    () => customTypes.find(({ name }) => name === "job_title")?.display_as,
    [customTypes]
  );

  const data = useMemo(
    () =>
      [
        { label: jobTitleLabel, text: getJobTitle(profile) },
        getMatchInfoData("match_info_1"),
        getMatchInfoData("match_info_2"),
        getMatchInfoData("match_info_3"),
        getMatchInfoData("match_info_4"),
        getMatchInfoData("match_info_5")
      ].filter((item) => !!item.text),
    [getMatchInfoData, profile, jobTitleLabel]
  );

  return (
    <div className={css.headline}>
      {data.map(({ label, text }, index) => (
        <Inline key={index} alignItems="center" gap="spacingMd">
          <Typography variant="labelRegular" title={label}>
            {truncate(label, { length: 14 })}
          </Typography>
          <Typography variant="labelBold" title={text} textOverflow="clip">
            {text}
          </Typography>
        </Inline>
      ))}
    </div>
  );
};
